import { Fragment, useState } from 'react'
import type { ApiKey } from '../types/api'
import { creatorLabel } from '../lib/creator'
import { deleteErrorText } from '../lib/deleteChain'
import { chainLastUsedLabel, secretLabel, secretState, type KeyChain } from '../lib/keyChains'
import { lastUsedLabel } from '../lib/lastUsed'
import { describeWarning, oldSecretWarnings } from '../lib/oldSecretWarning'
import { nextSort, sortKeyChains, type SortKey, type SortState } from '../lib/sortKeyChains'
import KeyBadges from './KeyBadges'
import LastUsedValue from './LastUsedValue'
import Timestamp from './Timestamp'

interface ApiKeyTableProps {
  chains: KeyChain[]
  userId: string | null
  usageTrackedSince: string | null
  now: Date
  deletingKeys: Map<string, string | null>
  newlyCreatedKeyId: string | null
  onDeleteChain?: (chain: KeyChain) => void
  onDeleteSecret?: (secret: ApiKey) => void
  onClearError: (chain: KeyChain) => void
  onShowUsage: (clientId: string, keyName: string) => void
}

const headerText = 'text-xs font-medium uppercase tracking-wide text-gray-500'
const cell = 'px-4 py-3 text-sm text-gray-700'
const subCell = 'px-4 py-2 text-xs text-gray-600'

export default function ApiKeyTable({
  chains,
  userId,
  usageTrackedSince,
  now,
  deletingKeys,
  newlyCreatedKeyId,
  onDeleteChain,
  onDeleteSecret,
  onClearError,
  onShowUsage,
}: ApiKeyTableProps) {
  const [sort, setSort] = useState<SortState | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const rows = sortKeyChains(chains, sort, usageTrackedSince)
  const showLastUsed = !!usageTrackedSince
  const columnCount = showLastUsed ? 7 : 6

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const sortableHeader = (label: string, key: SortKey, className = '') => {
    const direction = sort?.key === key ? (sort.descending ? 'descending' : 'ascending') : 'none'
    return (
      <th scope="col" aria-sort={direction} className={`px-4 py-3 text-left ${className}`}>
        <button
          type="button"
          onClick={() => setSort(nextSort(sort, key))}
          className={`inline-flex items-center gap-1 hover:text-gray-700 ${headerText}`}
        >
          {label}
          <span aria-hidden="true">{direction === 'ascending' ? '↑' : direction === 'descending' ? '↓' : ''}</span>
        </button>
      </th>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-card border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="border-b border-gray-100">
            <tr>
              <th scope="col" className={`px-4 py-3 text-left ${headerText}`}>Name</th>
              <th scope="col" className={`hidden md:table-cell px-4 py-3 text-left ${headerText}`}>Created by</th>
              <th scope="col" className={`hidden md:table-cell px-4 py-3 text-left ${headerText}`}>Secret</th>
              {sortableHeader('Created', 'created', 'hidden md:table-cell')}
              {sortableHeader('Expires', 'expires')}
              {showLastUsed && sortableHeader('Last used', 'lastUsed')}
              <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((chain) => {
              const apiKey = chain.current
              const deleteError = deleteErrorText(chain, deletingKeys)
              if (deleteError) {
                return (
                  <tr key={chain.id} className="bg-red-50">
                    <td colSpan={columnCount} className="px-4 py-3">
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm text-red-800">
                          <span className="font-medium">Delete failed for {apiKey.display_name}:</span> {deleteError}
                        </p>
                        <button
                          onClick={() => onClearError(chain)}
                          className="px-3 py-1.5 text-xs font-medium text-red-900 bg-white rounded-md hover:bg-red-100 transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              }
              const isDeleting = chain.secrets.some((s) => deletingKeys.get(s.id) === null)
              const warnings = oldSecretWarnings(chain, usageTrackedSince, now)
              const lastUsed = chainLastUsedLabel(chain, usageTrackedSince, now)
              const creator = creatorLabel(chain.createdBy, userId)
              const isChain = chain.secrets.length > 1
              const isOpen = expanded.has(chain.id)
              const isNew = chain.secrets.some((s) => s.id === newlyCreatedKeyId)
              const rowState = `${isDeleting ? 'opacity-50' : ''} ${isNew ? 'animate-pulse' : ''}`
              return (
                <Fragment key={chain.id}>
                  <tr className={rowState}>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {isChain ? (
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            aria-label={`${isOpen ? 'Hide' : 'Show'} the secrets of ${apiKey.display_name}`}
                            onClick={() => toggle(chain.id)}
                            className="w-4 text-gray-500 hover:text-gray-900"
                          >
                            {isOpen ? '▾' : '▸'}
                          </button>
                        ) : (
                          <span className="inline-block w-4" aria-hidden="true" />
                        )}
                        <span className="text-sm font-medium text-gray-900">{apiKey.display_name}</span>
                        <KeyBadges chain={chain} warnings={warnings} />
                      </div>
                    </td>
                    <td className={`hidden md:table-cell ${cell} ${creator.text === 'Not recorded' ? 'text-gray-500' : ''}`}>
                      <span title={creator.title}>{creator.text}</span>
                    </td>
                    <td className={`hidden md:table-cell font-mono ${cell}`}>{apiKey.hint ? `${apiKey.hint}…` : '—'}</td>
                    <td className={`hidden md:table-cell ${cell}`}><Timestamp value={chain.createdAt} /></td>
                    <td className={cell}><Timestamp value={apiKey.expires_at} /></td>
                    {showLastUsed && <td className={cell}>{lastUsed && <LastUsedValue label={lastUsed} />}</td>}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isDeleting ? (
                        <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                          <span className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent" />
                          Deleting...
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-4">
                          <button
                            onClick={() => onShowUsage(apiKey.client_id, apiKey.display_name)}
                            className="text-sm font-medium text-accent hover:text-accent-dark transition-colors"
                          >
                            How to Use
                          </button>
                          {onDeleteChain && (
                            <button
                              onClick={() => onDeleteChain(chain)}
                              className="text-sm font-medium text-red-600 hover:text-red-500"
                            >
                              Delete
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                  {warnings.map((warning) => {
                    const { title, body } = describeWarning(warning)
                    return (
                      <tr key={`warning-${warning.old.id}`}>
                        <td colSpan={columnCount} className="px-4 pb-3">
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                            <p className="text-xs font-medium text-amber-900">{title}</p>
                            <p className="mt-1 text-xs text-amber-800">{body}</p>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                  {isOpen &&
                    chain.secrets.map((secret) => {
                      const used = lastUsedLabel(secret, usageTrackedSince, now)
                      return (
                        <tr key={secret.id} className="bg-gray-50">
                          <td className="py-2 pl-12 pr-4 text-xs">
                            <span className="font-mono text-gray-900">{secretLabel(secret)}</span>{' '}
                            <span className="text-gray-600">{secretState(secret, chain)}</span>
                          </td>
                          <td className="hidden md:table-cell" />
                          <td className="hidden md:table-cell" />
                          <td className={`hidden md:table-cell ${subCell}`}><Timestamp value={secret.created_at} /></td>
                          <td className={subCell}><Timestamp value={secret.expires_at} /></td>
                          {showLastUsed && <td className={subCell}>{used && <LastUsedValue label={used} />}</td>}
                          <td className="px-4 py-2 text-right">
                            {onDeleteSecret && !isDeleting && (
                              <button
                                type="button"
                                onClick={() => onDeleteSecret(secret)}
                                aria-label={`Delete secret ${secretLabel(secret)}`}
                                className="text-xs font-medium text-red-600 hover:text-red-500"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
